import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Compass, ArrowLeft, Printer } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PrintSummaryPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [conversation, setConversation] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadConversation();
  }, [sessionId]);

  const loadConversation = async () => {
    try {
      const response = await axios.get(`${API}/chat/conversation/${sessionId}`);
      setConversation(response.data);
    } catch (error) {
      console.error("Error loading conversation:", error);
      toast.error("Failed to load conversation");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric"
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#EDF7F6] to-[#FAF9F6] flex items-center justify-center">
        <p className="text-lg text-text-secondary font-body">Loading conversation...</p>
      </div>
    );
  }

  if (!conversation) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#EDF7F6] to-[#FAF9F6] flex items-center justify-center">
        <div className="text-center">
          <p className="text-lg text-text-secondary font-body mb-4">Conversation not found</p>
          <Button
            onClick={() => navigate("/")}
            className="min-h-[48px] px-6 bg-primary text-white hover:bg-[#238C82] rounded-full font-bold"
          >
            Go Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header - Hidden on print */}
      <header className="no-print bg-white border-b border-[#E2E8F0] shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                data-testid="back-to-chat-button"
                onClick={() => navigate(`/chat/${sessionId}`)}
                variant="ghost"
                className="text-primary hover:bg-primary/5"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <h1 className="text-xl font-heading font-bold text-primary">Care Plan Summary</h1>
            </div>
            <Button
              data-testid="print-summary-button"
              onClick={handlePrint}
              className="min-h-[48px] px-6 bg-primary text-white hover:bg-[#238C82] rounded-full font-bold shadow-md hover:shadow-lg transition-all duration-300 active:scale-95 font-body"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
          </div>
        </div>
      </header>

      {/* Printable Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header for print */}
        <div className="mb-8 pb-6 border-b-2 border-[#E2E8F0]">
          <div className="flex items-center gap-3 mb-4">
            <img 
              src="/tyvena-mark-512.png" 
              alt="Asher" 
              className="w-16 h-16 rounded-full object-cover shadow-lg"
            />
            <div>
              <h1 className="text-3xl font-heading font-bold text-primary">Asher Care Navigation</h1>
              <p className="text-sm text-text-secondary font-body">Care Plan Summary</p>
            </div>
          </div>
          <div className="text-sm text-text-secondary font-body">
            <p>Date: {formatDate(conversation.created_at)}</p>
            <p>Session ID: {conversation.session_id}</p>
          </div>
        </div>

        {/* Care Type Recommendation */}
        {conversation.care_type_recommendation && (
          <div className="mb-8 p-6 bg-accent/5 border-l-4 border-l-accent rounded-lg">
            <h2 className="text-xl font-heading font-semibold text-text-primary mb-2">
              Recommended Care Type
            </h2>
            <p className="text-2xl font-heading font-bold text-accent">
              {conversation.care_type_recommendation}
            </p>
          </div>
        )}

        {/* Conversation Summary */}
        <div className="mb-8">
          <h2 className="text-2xl font-heading font-bold text-text-primary mb-6">
            Conversation Summary
          </h2>
          <div className="space-y-6">
            {conversation.messages.map((message, index) => (
              <div key={index} className="">
                <div className="flex items-start gap-3 mb-2">
                  {message.role === "assistant" && (
                    <img 
                      src="/tyvena-mark-512.png" 
                      alt="Asher" 
                      className="w-8 h-8 rounded-full object-cover mt-1"
                    />
                  )}
                  <div className="flex-1">
                    <p className="text-sm font-body font-semibold text-text-secondary mb-1">
                      {message.role === "assistant" ? "Asher" : "You"}
                    </p>
                    <p className="text-base font-body text-text-primary whitespace-pre-wrap leading-relaxed">
                      {message.content}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Next Steps */}
        <div className="p-6 bg-primary/5 rounded-lg">
          <h2 className="text-xl font-heading font-semibold text-text-primary mb-4">
            Next Steps
          </h2>
          <ul className="space-y-2 text-base font-body text-text-primary">
            <li className="flex items-start gap-2">
              <span className="text-primary mt-1">•</span>
              <span>Review the recommended care type and ensure it matches your needs</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-1">•</span>
              <span>Contact care providers in your area for consultations and pricing</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-1">•</span>
              <span>Verify insurance coverage and payment options with providers</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-1">•</span>
              <span>Keep this summary for reference during provider discussions</span>
            </li>
          </ul>
        </div>

        {/* Footer for print */}
        <div className="mt-12 pt-6 border-t-2 border-[#E2E8F0] text-center">
          <p className="text-sm text-text-secondary font-body">
            This summary was generated by Asher Care Navigation
          </p>
          <p className="text-sm text-text-secondary font-body">
            For more information, visit your Asher dashboard
          </p>
        </div>
      </main>
    </div>
  );
}
